import React from 'react'
import ManualAudioCaptureProviderV2 from './manual-audio-capture-provider-v2'
import type { WarningType } from './types'

export type DeviceType = {
  deviceId: string
  label: string
}

type TelehealthAudioSource = 'browser' | 'desktop'

interface AudioCaptureContextType {
  isReady: boolean
  audioInputs: DeviceType[]
  selectedAudioInput: string
  setSelectedAudioInput: (deviceId: string) => void
  testAudioInputs: () => Promise<void>
  hasMicAccess: boolean
  setHasMicAccess: React.Dispatch<React.SetStateAction<boolean>>
  promptForDevicePermissions: () => void
  isMuted: boolean
  toggleMute: () => void
  disableEchoCancelation: React.Dispatch<React.SetStateAction<boolean>>
  isRecording: boolean
  isRecordingLoading: boolean
  startRecording: ({
    isTelehealth,
    isUsingHeadphones,
    noteType,
    telehealthAudioSource,
    isHighQualityAudio
  }: {
    isTelehealth: boolean
    isUsingHeadphones: boolean
    noteType: string
    telehealthAudioSource: TelehealthAudioSource | null
    isHighQualityAudio: boolean | null
  }) => Promise<void>
  stopRecording: () => Promise<void>
  startContentShare: () => Promise<'canceled' | 'unsupported' | 'success'>
  stopContentShare: () => void
  isTabAudioStreamOpen: boolean
  logData: object
  isUsingHeadphones: boolean
  setIsUsingHeadphones: React.Dispatch<React.SetStateAction<boolean>>
  isTelehealth: boolean | null
  setIsTelehealth: React.Dispatch<React.SetStateAction<boolean | null>>
  telehealthAudioSource: TelehealthAudioSource | null
  setTelehealthAudioSource: React.Dispatch<
    React.SetStateAction<TelehealthAudioSource | null>
  >
  qualityToggle: boolean | null
  setQualityToggle: React.Dispatch<React.SetStateAction<boolean | null>>
  telehealthSourceError: string | null
  hasDetectedAudio: boolean
  showMicWarning: boolean
  showTabMicWarning: boolean
  showScreenShareWarning: boolean
  showAudioStreamWarning: boolean
  activeWarning: WarningType | null
  setActiveWarning: React.Dispatch<React.SetStateAction<WarningType | null>>
  isUploadComplete: boolean
  selectedAudioInputLabel: string | undefined
  isSettingsOpen: boolean
  setIsSettingsOpen: React.Dispatch<React.SetStateAction<boolean>>
  stopRecordingAfter: string
  setStopRecordingAfter: React.Dispatch<React.SetStateAction<string>>
  isStopRecordingAfterEnabled: boolean
  setIsStopRecordingAfterEnabled: (enabled: boolean) => void
  hasResolvedInputDevicePermissions: boolean
  permissionStatus: string | null
  uploadQueueSize: number
  contentShareDisplayType: string
  setContentShareDisplayType: React.Dispatch<React.SetStateAction<string>>
  isTelehealthSourceRequiredEnabled: boolean
}

export const AudioCaptureContext = React.createContext<
  AudioCaptureContextType | undefined
>(undefined)

interface AudioCaptureProviderProps {
  sessionId?: string
  refetchRecap?: () => void
  isDictation?: boolean
  children: React.ReactNode
}

export const AudioCaptureProvider: React.FC<AudioCaptureProviderProps> = ({
  sessionId,
  refetchRecap,
  isDictation = false,
  children
}) => {
  return (
    <ManualAudioCaptureProviderV2
      sessionId={sessionId}
      refetchRecap={refetchRecap}
      isDictation={isDictation}
    >
      {children}
    </ManualAudioCaptureProviderV2>
  )
}

export default AudioCaptureProvider
